import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';

const roleMap = {
  0: 'Client',
  1: 'Client (Muted)',
  2: 'Router',
  4: 'Repeater',
  5: 'Tracker',
  6: 'Sensor',
  7: 'TAK Gateway',
  8: 'Client (Hidden)',
  9: 'Lost & Found',
  10: 'TAK Tracker'
};

const hardwareMap = {
  1: 'Generic ESP32',
  2: 'Heltec LoRa 32 v2',
  3: 'LILYGO T-Beam',
  4: 'LILYGO LoRa32',
  5: 'LILYGO T-Echo',
  6: 'Heltec Stick Lite',
  7: 'T-Watch S3',
  8: 'RAK WisBlock 4601',
  9: 'RAK WisBlock 4630',
  10: 'Seeed Wio-WM1110',
  11: 'Pi Pico + LoRa',
  12: 'Raspberry Pi + HAT',
  13: 'Heltec Vision Master',
  14: 'CanaryOne Mini',
  15: 'Nano Gateway',
  16: 'B&Q Station'
};

function NodeInfoDashboard({ darkMode }) {
  const { fromId } = useParams();
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [messages, setMessages] = useState([]);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [presetRange, setPresetRange] = useState('86400000');
  const [modalChartData, setModalChartData] = useState(null);
  const [modalChartOptions, setModalChartOptions] = useState(null);

  useEffect(() => {
    fetch('/api/mqtt/nodeinfo/list')
      .then(res => res.json())
      .then(data => {
        setNodes(data);
        if (fromId) {
          const match = data.find(n => n.parsedMessage.from.toString() === fromId);
          if (match) {
            setSelectedNode(match);
            fetchNodeData(match);
          }
        }
      })
      .catch(console.error);
  }, [fromId]);

  const selectNode = (node) => {
    if (selectedNode && selectedNode._id === node._id) {
      setSelectedNode(null);
      setTelemetry([]);
      setMessages([]);
      return;
    }

    setSelectedNode(node);
    fetchNodeData(node);
  };

  const fetchNodeData = (node) => {
    const from = node.parsedMessage.from;
    const end = customEnd ? new Date(customEnd).toISOString() : new Date().toISOString();
    const start = customStart
      ? new Date(customStart).toISOString()
      : new Date(Date.now() - Number(presetRange)).toISOString();

    fetch(`/api/mqtt/telemetry?from=${from}&timestampStart=${start}&timestampEnd=${end}`)
      .then(res => res.json())
      .then(setTelemetry)
      .catch(console.error);

    fetch(`/api/mqtt/text?from=${from}`)
      .then(res => res.json())
      .then(setMessages)
      .catch(console.error);
  };

  const formatDualChartData = () => {
    return {
      labels: telemetry.map(t => new Date(t.timestamp).toLocaleString()),
      datasets: [
        {
          label: 'Voltage (V)',
          data: telemetry.map(t => t.parsedMessage.payload.voltage),
          borderColor: 'blue',
          tension: 0.1,
          yAxisID: 'y'
        },
        {
          label: 'Battery Level (%)',
          data: telemetry.map(t => t.parsedMessage.payload.battery_level),
          borderColor: 'green',
          tension: 0.1,
          yAxisID: 'y1'
        }
      ]
    };
  };

  const dualYAxisOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        labels: {
          color: darkMode ? 'white' : 'black'
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false
      }
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Voltage (V)', color: 'blue' },
        ticks: { color: 'blue' }
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Battery Level (%)', color: 'green' },
        ticks: { color: 'green' },
        grid: { drawOnChartArea: false }
      },
      x: {
        ticks: { color: darkMode ? 'white' : 'black' }
      }
    }
  };

  const openChartModal = (data, options = null) => {
    setModalChartData(data);
    setModalChartOptions(options);
  };

  const closeModal = () => {
    setModalChartData(null);
    setModalChartOptions(null);
  };

  const formatChartData = (key, labelOverride = null, fromRoot = false) => {
    return {
      labels: telemetry.map(t => new Date(t.timestamp).toLocaleString()),
      datasets: [
        {
          label: labelOverride || key,
          data: telemetry.map(t => fromRoot ? t.parsedMessage[key] : t.parsedMessage.payload[key]),
          fill: false,
          tension: 0.1
        }
      ]
    };
  };

  return (
    <div className={`max-w-7xl mx-auto p-6 pb-16 ${darkMode ? 'text-white bg-gray-800' : 'text-black bg-white'}`}>
      <h2 className="text-2xl font-bold mb-4">Nodes</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {nodes.map(node => (
          <div
            key={node._id}
            className={`border rounded p-4 cursor-pointer shadow hover:shadow-lg ${selectedNode && selectedNode._id === node._id ? 'ring-2 ring-blue-500' : ''} bg-white dark:bg-gray-700`}
            onClick={() => selectNode(node)}
          >
            <p><strong>{node.parsedMessage.payload.longname}</strong> ({node.parsedMessage.payload.shortname})</p>
            <p>Role: {roleMap[node.parsedMessage.payload.role] || `Unknown (${node.parsedMessage.payload.role})`}</p>
            <p>RSSI: {node.parsedMessage.rssi}</p>
            <p>Hops Away: {node.parsedMessage.hops_away}</p>
            <p>Hardware: {hardwareMap[node.parsedMessage.payload.hardware] || `Unknown (${node.parsedMessage.payload.hardware})`}</p>
          </div>
        ))}
      </div>

      {selectedNode && (
        <div className="mt-8">
          <h3 className="text-xl font-bold mb-2">Telemetry for {selectedNode.parsedMessage.payload.longname}</h3>
          <div className="flex flex-wrap gap-4 mb-4 items-center">
            <label className="flex flex-col">
              Preset Range:
              <select
                value={presetRange}
                onChange={(e) => {
                  setPresetRange(e.target.value);
                  setCustomStart(null);
                  setCustomEnd(null);
                }}
                className="border rounded px-2 py-1 bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                <option value={86400000}>Last 1 day</option>
                <option value={7 * 86400000}>Last 7 days</option>
                <option value={30 * 86400000}>Last 30 days</option>
                <option value={365 * 86400000}>Last 1 year</option>
              </select>
            </label>
            <label className="flex flex-col">
              Start:
              <input
                type="datetime-local"
                className="border rounded px-2 py-1 bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                value={customStart || ''}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setPresetRange('');
                }}
              />
            </label>
            <label className="flex flex-col">
              End:
              <input
                type="datetime-local"
                className="border rounded px-2 py-1 bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                value={customEnd || ''}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setPresetRange('');
                }}
              />
            </label>
            <button onClick={() => fetchNodeData(selectedNode)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mt-6">Reload</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="overflow-x-auto cursor-pointer" onClick={() => openChartModal(formatDualChartData(), dualYAxisOptions)}>
              <Line data={formatDualChartData()} options={dualYAxisOptions} />
            </div>
            <div className="overflow-x-auto cursor-pointer" onClick={() => openChartModal(formatChartData('rssi', 'RSSI', true))}>
              <Line data={formatChartData('rssi', 'RSSI', true)} />
            </div>
            <div className="overflow-x-auto cursor-pointer" onClick={() => openChartModal(formatChartData('channel_utilization'))}>
              <Line data={formatChartData('channel_utilization')} />
            </div>
            <div className="overflow-x-auto cursor-pointer" onClick={() => openChartModal(formatChartData('uptime_seconds', 'uptime (days)'))}>
              <Line
                data={formatChartData('uptime_seconds', 'uptime (days)')}
                options={{
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => `${(value / 86400).toFixed(2)} d`
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          {modalChartData && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-900 p-6 rounded-lg max-w-5xl w-full relative">
                <button onClick={closeModal} className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded px-3 py-1">Close</button>
                <Line
                  data={modalChartData}
                  options={modalChartOptions || { responsive: true, maintainAspectRatio: false }}
                  height={200}
                />
              </div>
            </div>
          )}

          <h3 className="text-xl font-bold mt-10 mb-2">Latest Messages</h3>
          {messages.map(msg => (
            <div key={msg._id} className="mb-2 p-3 border rounded bg-white dark:bg-gray-700">
              <p className="text-sm text-gray-400">{new Date(msg.timestamp).toLocaleString()}</p>
              <p>{msg.parsedMessage.payload.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default NodeInfoDashboard;
